import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Globe, Zap, BookOpen, ShoppingCart, Gem } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { RootState } from '../store';
import { setLanguage } from '@digitaltattoo/redux-slices';
import UserMenu from './UserMenu';
import { useTestMode } from '../hooks/useTestMode';
import { useFrontoffWorkingMode } from '../hooks/useSystemStatus';
import { appConfig } from '../config/app';

export function Header() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentLanguage = useSelector((state: RootState) => state.language.current);
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isTestMode } = useTestMode();
  const { isLimited, features } = useFrontoffWorkingMode();
  const { totalItems } = useCart();
  const showCaddy = appConfig.bookingViaCaddy;

  const isAccountRoute = location.pathname === '/account' || location.pathname.startsWith('/account/');
  const isBookingRoute =
    location.pathname === '/book-piercing' || location.pathname === '/book-tattoo';

  const handleLanguageChange = (lang: 'en' | 'fr') => {
    i18n.changeLanguage(lang);
    dispatch(setLanguage(lang));
  };

  useEffect(() => {
    if (isLimited) {
      handleLanguageChange('fr');
    }
  }, [isLimited]);

  const navigation = [
    { name: t('navigation.home'), href: '/' },
    { name: t('navigation.services'), href: '#services' },
    { name: t('navigation.gallery'), href: '#gallery' },
    { name: t('navigation.about'), href: '#about' },
    // { name: t('navigation.contact'), href: '#contact' },
    // { name: t('navigation.boutique'), href: '/boutique' },
  ];

  const handleNavigation = (href: string) => {
    const target = href.startsWith('/') ? href : `/${href}`;
    navigate(target);
    setIsMobileMenuOpen(false);
    if (!target.includes('#')) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <header className={`fixed left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm transition-[top] duration-200 ${isTestMode ? 'top-9' : 'top-0'}`}>
      <nav className="w-full">
        {/* Desktop: account pages use a 2-column header aligned with the account sidebar (w-64) */}
        {isAccountRoute && (
          <div className="hidden lg:flex w-full items-center">
            {/* Left column (same width as account sidebar) */}
            <div className="w-64 px-6 py-4 flex items-center">
              <button type="button" onClick={() => handleNavigation('/')} className="flex items-center space-x-2 text-left">
                <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-primary-300 to-accent-500 rounded-lg">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-base font-semibold text-textPrimary leading-tight">
                    Digital Tattoo
                  </p>
                  <p className="text-xs text-textSecondary -mt-1">By Abraxas</p>
                </div>
              </button>
            </div>

            {/* Right column (aligned with account content padding) */}
            <div className="flex-1 px-8 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-8">
                {navigation.map((item) => (
                  <button
                    type="button"
                    key={item.name}
                    onClick={() => handleNavigation(item.href)}
                    className="fo-nav-link text-textSecondary hover:text-primary-400 transition-colors duration-200 font-medium"
                  >
                    {item.name}
                  </button>
                ))}
              </div>

              <div className="flex items-center space-x-4">
                {/* Cart icon */}
                {/* <button
                  onClick={() => navigate('/panier')}
                  className="relative p-2 text-textSecondary hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors"
                  aria-label="Panier"
                >
                  <ShoppingCart className="w-5 h-5" />
                  {totalItems > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary-300 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {totalItems > 9 ? '9+' : totalItems}
                    </span>
                  )}
                </button> */}

                {/* Help */}
                {features.userGuide && (
                  <a
                    href="/docs/manual/fr/index.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-textSecondary hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors"
                    title="Guide utilisateur"
                    aria-label="Aide"
                  >
                    <BookOpen className="w-5 h-5" />
                  </a>
                )}

                {features.languageSwitch && <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => handleLanguageChange('en')}
                    className={`px-2 py-1 text-sm font-medium rounded transition-colors ${
                      currentLanguage === 'en'
                        ? 'bg-white text-primary-500 shadow-sm'
                        : 'text-textSecondary hover:text-textPrimary'
                    }`}
                  >
                    EN
                  </button>
                  <button
                    onClick={() => handleLanguageChange('fr')}
                    className={`px-2 py-1 text-sm font-medium rounded transition-colors ${
                      currentLanguage === 'fr'
                        ? 'bg-white text-primary-500 shadow-sm'
                        : 'text-textSecondary hover:text-textPrimary'
                    }`}
                  >
                    FR
                  </button>
                </div>}

                {isAuthenticated && user ? (
                  <UserMenu user={user} />
                ) : (
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => handleNavigation('/login')}
                      className="fo-nav-link text-textSecondary hover:text-primary-500 transition-colors font-medium"
                    >
                      {t('navigation.login')}
                    </button>
                    <button onClick={() => handleNavigation('/register')} className="btn-primary">
                      {t('navigation.register')}
                    </button>
                  </div>
                )}
                
              </div>
            </div>
          </div>
        )}

        {/* Default layout (public pages + mobile/tablet account pages) */}
        <div className={isAccountRoute ? 'lg:hidden' : ''}>
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className={`flex justify-between items-center ${isBookingRoute ? 'py-2.5 md:py-4' : 'py-4'}`}>
              {/* Logo */}
              <button type="button" onClick={() => handleNavigation('/')} className="flex items-center space-x-2 text-left">
                <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-primary-300 to-accent-500 rounded-lg">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div className={isBookingRoute ? 'hidden lg:block' : 'hidden sm:block'}>
                  <p className="text-base font-semibold text-textPrimary leading-tight">
                    Digital Tattoo
                  </p>
                  <p className="text-xs text-textSecondary -mt-1">By Abraxas</p>
                </div>
              </button>

              {/* Desktop Navigation */}
              <div className="hidden lg:flex items-center space-x-8">
                {navigation.map((item) => (
                  <button
                    type="button"
                    key={item.name}
                    onClick={() => handleNavigation(item.href)}
                    className="fo-nav-link text-textSecondary hover:text-primary-400 transition-colors duration-200 font-medium"
                  >
                    {item.name}
                  </button>
                ))}
                {/* <button
                  onClick={() => navigate('/become-partner')}
                  className="text-textSecondary hover:text-primary-500 transition-colors duration-200 font-medium"
                >
                  Devenir partenaire
                </button> */}
              </div>

              {/* Language Switcher & Auth */}
              <div className="flex items-center space-x-4">
                {/* Cart icon */}
                {/* <button
                  onClick={() => navigate('/panier')}
                  className="relative p-2 text-textSecondary hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors"
                  aria-label="Panier"
                >
                  <ShoppingCart className="w-5 h-5" />
                  {totalItems > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary-300 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {totalItems > 9 ? '9+' : totalItems}
                    </span>
                  )}
                </button> */}

                {/* Help */}
                {features.userGuide && (
                  <a
                    href="/docs/manual/fr/index.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hidden lg:flex p-2 text-textSecondary hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors"
                    title="Guide utilisateur"
                    aria-label="Aide"
                  >
                    <BookOpen className="w-5 h-5" />
                  </a>
                )}

                {features.languageSwitch && <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => handleLanguageChange('en')}
                    className={`px-2 py-1 text-sm font-medium rounded transition-colors ${
                      currentLanguage === 'en'
                        ? 'bg-white text-primary-500 shadow-sm'
                        : 'text-textSecondary hover:text-textPrimary'
                    }`}
                  >
                    EN
                  </button>
                  <button
                    onClick={() => handleLanguageChange('fr')}
                    className={`px-2 py-1 text-sm font-medium rounded transition-colors ${
                      currentLanguage === 'fr'
                        ? 'bg-white text-primary-500 shadow-sm'
                        : 'text-textSecondary hover:text-textPrimary'
                    }`}
                  >
                    FR
                  </button>
                </div>}

                {/* Authentication Section */}
                {isAuthenticated && user ? (
                  <UserMenu user={user} />
                ) : (
                  <div className="flex items-center space-x-3">
                    <button 
                      onClick={() => handleNavigation('/login')}
                      className={`fo-nav-link text-textSecondary hover:text-primary-500 transition-colors font-medium ${isBookingRoute ? 'hidden lg:block' : 'hidden sm:block'}`}
                    >
                      {t('navigation.login')}
                    </button>
                    <button 
                      onClick={() => handleNavigation('/register')}
                      className={`btn-primary ${isBookingRoute ? 'hidden lg:block' : 'hidden sm:block'}`}
                    >
                      {t('navigation.register')}
                    </button>
                  </div>
                )}
                
                {/* Mobile cart — only when booking via caddy is enabled */}
                {showCaddy && (
                  <button
                    onClick={() => navigate('/panier')}
                    className="lg:hidden relative p-2 rounded-md text-textSecondary hover:text-primary-500 hover:bg-gray-100 transition-colors"
                    aria-label="Panier"
                  >
                    <ShoppingCart className="w-5 h-5" />
                    {totalItems > 0 && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary-300 text-white text-xs font-bold rounded-full flex items-center justify-center">
                        {totalItems > 9 ? '9+' : totalItems}
                      </span>
                    )}
                  </button>
                )}
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="lg:hidden p-2 rounded-md text-textSecondary hover:text-textPrimary hover:bg-gray-100 transition-colors"
                >
                  {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </div>
            </div>

            {/* Mobile Navigation */}
            {isMobileMenuOpen && (
              <div className="lg:hidden border-t border-gray-200 py-4 animate-fade-in">
                <div className="flex flex-col space-y-4">
                  {navigation.map((item) => (
                    <button
                      type="button"
                      key={item.name}
                      className="fo-nav-link text-left text-textSecondary hover:text-primary-400 transition-colors duration-200 font-medium py-2"
                      onClick={() => handleNavigation(item.href)}
                    >
                      {item.name}
                    </button>
                  ))}
                  {features.boutique && (
                    <button
                      onClick={() => handleNavigation('/boutique')}
                      className="fo-nav-link flex items-center gap-2 text-left text-textSecondary hover:text-primary-500 transition-colors duration-200 font-medium py-2"
                    >
                      <Gem className="w-4 h-4" />
                      Boutique
                    </button>
                  )}
                  {features.becomePartner && (
                    <button
                      onClick={() => handleNavigation('/become-partner')}
                      className="fo-nav-link text-left text-textSecondary hover:text-primary-500 transition-colors duration-200 font-medium py-2"
                    >
                      Devenir partenaire
                    </button>
                  )}

                  {features.userGuide && (
                    <a
                      href="/docs/manual/fr/index.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="fo-nav-link flex items-center gap-2 text-textSecondary hover:text-primary-500 transition-colors duration-200 font-medium py-2"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <BookOpen className="w-4 h-4" />
                      Guide utilisateur
                    </a>
                  )}

                  {/* Mobile Auth Buttons */}
                  {isAuthenticated && user ? (
                    <div className="pt-4 border-t border-gray-200">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-8 h-8 bg-gradient-to-br from-primary-300 to-accent-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm font-medium">
                            {user.firstName.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-textPrimary">
                            {user.firstName} {user.lastName}
                          </p>
                          <p className="text-xs text-textSecondary">{user.email}</p>
                        </div>
                      </div>
                      <button 
                        onClick={() => {
                          navigate('/account');
                          setIsMobileMenuOpen(false);
                        }}
                        className="btn-primary w-full mb-2"
                      >
                        {t('navigation.account')}
                      </button>
                    </div>
                  ) : (
                    <div className="pt-4 border-t border-gray-200 space-y-2">
                      <button 
                        onClick={() => handleNavigation('/login')}
                        className="fo-nav-link w-full py-2 text-textSecondary hover:text-primary-500 transition-colors font-medium"
                      >
                        {t('navigation.login')}
                      </button>
                      <button 
                        onClick={() => handleNavigation('/register')}
                        className="btn-primary w-full mb-2"
                      >
                        {t('navigation.register')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>
    </header>
  );
}